import pRetry, { AbortError } from 'p-retry';
import { useEffect, useMemo } from 'react';
import { getRemotePromise, useRuntime } from '../..';
/**
 * Determines if any of the remotes refetched successfully.
 *
 * @param {PromiseSettledResult<void>[]} results Results of the refetch promise.
 * @return Boolean indicating if some remotes refetched successully or not.
 */
function didSomeRemotesRefetchSuccessfully(results) {
    return results.some(result => result.status === 'fulfilled');
}
/**
 * Refetch remotes that failed to fetch on initial load.
 */
export function useRemoteRetry() {
    const runtime = useRuntime();
    const remotesToRetry = useMemo(() => runtime.failedToFetchRemotes, [runtime.failedToFetchRemotes]);
    async function refetchRemotes(cancelRequest) {
        // If there are no remotes to retry, return without rerendering
        if (remotesToRetry.length === 0) {
            return;
        }
        // Retry each remote up to 3 times
        const retryPromises = remotesToRetry.map(async (remote, index) => pRetry(async () => {
            await getRemotePromise(remote, remotesToRetry, runtime, {}, index, undefined, cancelRequest);
            if (cancelRequest.signal.aborted) {
                throw new AbortError('Request aborted');
            }
        }, { retries: 3 }));
        // Wait for all promises to settle
        const refetchResult = didSomeRemotesRefetchSuccessfully(await Promise.allSettled(retryPromises));
        // If some remotes refetched successfully, clear the list of remotes to retry
        // so that we don't retry them again on the next render
        // will cause a rerender in component using this hook
        if (refetchResult) {
            runtime.setFailedToFetchRemotes([]);
        }
    }
    useEffect(() => {
        const cancelRequest = new AbortController();
        refetchRemotes(cancelRequest).catch(err => {
            console.error(err);
        });
        return () => {
            cancelRequest.abort(); // On unmount, abort request
        };
    }, [remotesToRetry]);
}
